import Credentials from './Credentials';
import User from './User';
import CookieStorage from './CookieStorage';
const defaultHeaders = {
  'Content-Type': 'application/json;charset=UTF-8'
};
const defaultOptions = {
  baseUrl: 'https://auth.vetster.com/v1/',
  refreshTtl: 43800
};

/**
 * The Axios instance doesn't contain the package.json version, so
 * attempt to detect a function that only exists in axios:^1.0+.
 *
 * @param {object|null} axios
 * @returns {boolean}
 */
const isAxiosLegacy = function (axios) {
  return !!(axios && typeof axios.AxiosHeaders !== 'function');
};
const axiosOptions = function (clientOptions) {
  const options = {
    baseURL: clientOptions.baseUrl,
    timeout: 10000
  };
  if (isAxiosLegacy(clientOptions)) {
    options.defaults = {
      headers: {
        common: defaultHeaders
      }
    };
  } else {
    options.headers = defaultHeaders;
  }
  return options;
};

/**
 * Lodash pick() request parameters for a payload.
 *
 * @param {object} payload
 * @param {String[]} keys
 * @returns {object}
 */
const pick = function (payload, keys) {
  const params = {};
  keys.forEach(key => {
    if (key in payload) {
      params[key] = payload[key] || null;
    }
  });
  return params;
};
export default class AuthService {
  constructor(options) {
    if (!options) {
      throw new Error('Please initialise service with options.');
    }
    options = {
      ...defaultOptions,
      ...options
    };
    this.md5 = options.md5;
    this.keyPrefix = `v-auth${options.environment !== 'production' ? '-dev' : ''}`;
    this.lastUserKey = `${this.keyPrefix}.user-last-auth`;
    this.storage = options.storage || new CookieStorage(options.cookieStorage);

    // Implements StorageInterface.js: Use `null` to use LocalStorage / In-memory Storage
    this.user = null;

    // Instance of User.js
    this.authenticated = false;

    // NOTE: Must be kept in sync with `vetster/vetster-auth/.env/JWT_REFRESH_TTL`
    this.refreshTtl = options.refreshTtl;
    this.$authClient = this.setupAuthClient(options);
    this.userAgent = options.userAgent;
  }
  setupAuthClient(options) {
    if (!options.axios) {
      throw new Error('Please initialize service with Axios.');
    }
    const authClient = options.axios.create(axiosOptions(options));
    authClient.interceptors.request.use(async config => {
      const headers = isAxiosLegacy(options.axios) ? config.headers.common : config.headers;
      if (options.userAgent) {
        headers['User-Agent'] = options.userAgent;
      }
      if (options.appLocale) {
        headers['Accept-Language'] = options.appLocale;
        headers['X-Vetster-Locale'] = options.appLocale;
      }
      if (options.timeZoneName) {
        headers['X-Vetster-Browser-Timezone'] = options.timeZoneName;
      }
      if (options.cfTokenId && options.cfTokenSecret) {
        headers['CF-Access-Client-Id'] = options.cfTokenId;
        headers['CF-Access-Client-Secret'] = options.cfTokenSecret;
      }
      if (options.mobileVersion) {
        headers['X-Vetster-Mobile-Version'] = options.mobileVersion;
      }
      if (options.application) {
        headers['X-Vetster-Application'] = options.application;
      }
      if (options.uat) {
        headers['X-Vetster-UATID'] = options.uat;
      }
      if (options.platform) {
        headers['X-Vetster-Platform'] = options.platform;
      }
      if (typeof options.interceptor === 'function') {
        options.interceptor(config);
      }
      return config;
    });
    return authClient;
  }
  isAuthenticated() {
    return !!this.authenticated;
  }
  isGuest() {
    return !this.authenticated;
  }
  async getAccessToken() {
    const isAuthenticated = this.authenticated || (await this.authenticate());
    if (isAuthenticated) {
      const token = await this.getCurrentAccessToken();
      return token;
    }
    return null;
  }
  async getCurrentAccessToken() {
    const session = await this.user.getSession();
    if (session) {
      return session.getAccessToken().getToken();
    }
    return null;
  }
  async authenticate() {
    const currentUser = await this.getCurrentCachedUser();
    if (currentUser) {
      const currentUserSession = await currentUser.getSession();
      if (currentUserSession) {
        this.authenticated = true;
        return this.authenticated;
      }
    }
    this.authenticated = false;
    return this.authenticated;
  }
  async getCurrentCachedUser() {
    const username = await this.storage.getItem(this.lastUserKey);
    if (username) {
      const user = new User({
        authService: this,
        md5: this.md5,
        refreshTtl: this.refreshTtl,
        requiresPassword: username === this.user?.username && this.user.requiresPassword,
        storage: this.storage,
        username
      });
      this.user = user;
      return user;
    }
    return null;
  }
  async loginUsingToken(token) {
    if (this.user) {
      await this.user.setSession(this.user.createSession(token));
      this.authenticated = true;
    }
  }
  async registerUser(payload) {
    try {
      const response = await this.$authClient.post('register', payload);
      this.setUser(payload.email);
      await this.loginUsingToken(response.data.data.access_token);
      return true;
    } catch (err) {
      throw err;
    }
  }
  async authService(endpoint, payload) {
    const accessToken = await this.getAccessToken();
    try {
      return await this.$authClient.post(`${endpoint}`, payload, this.axiosOptions(accessToken));
    } catch (err) {
      this.authenticated = false;
      throw err;
    }
  }
  async requestUpdateEmailCode(payload) {
    const accessToken = await this.getAccessToken();
    try {
      await this.$authClient.post('update-email', {
        email_new: payload.email_new
      }, this.axiosOptions(accessToken));
    } catch (err) {
      throw err;
    }
  }
  async verifyUpdateEmailCode(payload) {
    const accessToken = await this.getAccessToken();
    try {
      await this.$authClient.put(`update-email`, {
        code: payload.code
      }, this.axiosOptions(accessToken));
    } catch (err) {
      throw err;
    }
  }
  async requestEmailCode() {
    const accessToken = await this.getAccessToken();
    try {
      await this.$authClient.post('verify-email', {}, this.axiosOptions(accessToken));
    } catch (err) {
      throw err;
    }
  }
  async requestEmailVerificationCode(payload = {}) {
    const accessToken = await this.getAccessToken();
    const params = pick(payload, ['access_token', 'provider_type']);
    try {
      await this.$authClient.post('email/verify', params, this.axiosOptions(accessToken));
    } catch (err) {
      throw err;
    }
  }
  async verifyEmailCode(payload) {
    try {
      await this.$authClient.put(`verify-email/${payload.id}`, {
        code: payload.code
      });
    } catch (err) {
      throw err;
    }
  }
  async verifyEmailVerificationCode(payload) {
    const accessToken = await this.getAccessToken();
    const params = pick(payload, ['access_token', 'code', 'provider_type']);
    try {
      await this.$authClient.put('email/verify', params, this.axiosOptions(accessToken));
    } catch (err) {
      throw err;
    }
  }
  async requestMagicLogin(payload) {
    try {
      const response = await this.$authClient.post('/magic-login', {
        url: payload && payload.url || null
      }, this.axiosOptions(await this.getAccessToken()));
      return response.data.data.url || response.data.data.code;
    } catch (err) {
      throw err;
    }
  }
  async verifyMagicLogin(payload) {
    try {
      const response = await this.$authClient.put(`/magic-login/${payload.id}`, {
        code: payload.code,
        platform: payload.platform
      });
      this.setUser(response.data.data.email);
      await this.loginUsingToken(response.data.data.access_token);
    } catch (err) {
      this.authenticated = false;
      throw err;
    }
  }
  async requestResetCode(payload) {
    try {
      await this.$authClient.post('reset-password', payload);
    } catch (err) {
      throw err;
    }
  }
  async resetPassword(payload) {
    try {
      await this.$authClient.put('reset-password', payload);
    } catch (err) {
      throw err;
    }
  }
  async changePassword(payload) {
    const accessToken = await this.getAccessToken();
    try {
      await this.$authClient.put('password', payload, this.axiosOptions(accessToken));
    } catch (err) {
      throw err;
    }
  }
  async addPassword(payload) {
    const accessToken = await this.getAccessToken();
    try {
      await this.$authClient.post('password', payload, this.axiosOptions(accessToken));
      this.user.setRequiresPassword(false);
    } catch (err) {
      throw err;
    }
  }
  async signOut() {
    this.user && (await this.user.clearUserTokens());
    this.authenticated = false;
    return true;
  }
  async refreshToken(accessToken) {
    try {
      const response = await this.$authClient.post('refresh', {}, this.axiosOptions(accessToken));
      return response.data.data.access_token;
    } catch (err) {
      await this.signOut(false);
    }
  }
  setUser(email) {
    this.user = new User({
      authService: this,
      email,
      md5: this.md5,
      refreshTtl: this.refreshTtl,
      storage: this.storage
    });
    try {
      window.localStorage.temporaryEmail = email;
    } catch {
      // Local storage not available
    }
  }
  async loginUser(payload) {
    const credentials = new Credentials(payload);
    const email = credentials.getEmail();
    try {
      const response = await this.$authClient.post('login', {
        email,
        password: credentials.getPassword(),
        code: payload.code,
        platform: payload.platform
      });
      this.setUser(email);
      await this.loginUsingToken(response.data.data.access_token);
    } catch (err) {
      this.authenticated = false;
      throw err;
    }
  }
  async loginUserWithoutPassword(payload) {
    const email = new Credentials({
      email: payload.email
    }).getEmail();
    try {
      this.setUser(email);
      await this.loginUsingToken(payload.access_token);
      this.user.setRequiresPassword();
    } catch (err) {
      this.authenticated = false;
      throw err;
    }
  }
  async loginViaSocialite(payload) {
    try {
      const response = await this.$authClient.post('auth/login', payload);
      this.setUser(response.data.data.email);
      await this.loginUsingToken(response.data.data.access_token);
    } catch (err) {
      this.authenticated = false;
      throw err;
    }
  }
  async loginViaSocialiteV2(payload) {
    try {
      const response = await this.$authClient.post('auth/v2/login', payload);
      this.setUser(response.data.data.email);
      await this.loginUsingToken(response.data.data.access_token);
    } catch (err) {
      this.authenticated = false;
      throw err;
    }
  }
  async oidcAuth(payload) {
    try {
      return await this.$authClient.post('oidc/auth', payload);
    } catch (err) {
      this.authenticated = false;
      throw err;
    }
  }
  async oidcLogin(payload) {
    try {
      const response = await this.$authClient.post('oidc/login', payload);
      this.setUser(response.data.data.email);
      await this.loginUsingToken(response.data.data.access_token);
    } catch (err) {
      this.authenticated = false;
      throw err;
    }
  }
  async requestOidcChallengeCode(payload) {
    try {
      return await this.$authClient.post('oidc/verify', payload);
    } catch (err) {
      this.authenticated = false;
      throw err;
    }
  }
  async verifyOidcChallengeCode(payload) {
    try {
      const response = await this.$authClient.put('oidc/verify', payload);
      this.setUser(response.data.data.email);
      await this.loginUsingToken(response.data.data.access_token);
    } catch (err) {
      this.authenticated = false;
      throw err;
    }
  }
  async registerViaSocialite(payload) {
    try {
      const response = await this.$authClient.post('auth/register', payload);
      this.setUser(response.data.data.email);
      await this.loginUsingToken(response.data.data.access_token);
    } catch (err) {
      this.authenticated = false;
      throw err;
    }
  }
  async registerViaSocialiteV2(payload) {
    try {
      const response = await this.$authClient.post('auth/v2/register', payload);
      this.setUser(response.data.data.email);
      await this.loginUsingToken(response.data.data.access_token);
    } catch (err) {
      this.authenticated = false;
      throw err;
    }
  }
  requiresPassword() {
    return this.user?.requiresPassword || false;
  }
  async uniqueEmail(payload) {
    try {
      return await this.$authClient.get(`auth/unique-email?provider_type=${payload.provider_type}&access_token=${payload.access_token}`);
    } catch (err) {
      this.authenticated = false;
      throw err;
    }
  }
  async authSocialite(payload) {
    try {
      const response = await this.$authClient.get('auth/social?' + this.authSocialiteParameters(payload));
      if (response.data.data.email && response.data.data.access_token) {
        this.setUser(response.data.data.email);
        await this.loginUsingToken(response.data.data.access_token);
      }
      return response;
    } catch (err) {
      this.authenticated = false;
      throw err;
    }
  }
  authSocialiteParameters(payload) {
    const parameters = `&provider_type=${payload.provider_type}&access_token=${payload.access_token}`;
    if (payload.platform) {
      return parameters + `&platform=${payload.platform}`;
    }
    return parameters;
  }
  async storeSocialite(payload) {
    const accessToken = await this.getAccessToken();
    try {
      return await this.$authClient.post('auth/providers', payload, this.axiosOptions(accessToken));
    } catch (err) {
      this.authenticated = false;
      throw err;
    }
  }
  async removeSocialite(payload) {
    const accessToken = await this.getAccessToken();
    try {
      return await this.$authClient.delete(`auth/providers/${payload.uuid}`, this.axiosOptions(accessToken));
    } catch (err) {
      this.authenticated = false;
      throw err;
    }
  }
  async getUserProviders() {
    const accessToken = await this.getAccessToken();
    try {
      const response = await this.$authClient.get('auth/providers', this.axiosOptions(accessToken));
      return response;
    } catch (err) {
      throw err;
    }
  }
  async authenticateOrganization(payload) {
    try {
      const response = await this.$authClient.get(`${payload.organizationName}/auth/callback?code=${payload.code}&platform=${payload.platform}`);
      if (response.data.data.email && response.data.data.access_token) {
        this.setUser(response.data.data.email);
        await this.loginUsingToken(response.data.data.access_token);
      }
      return response;
    } catch (err) {
      this.authenticated = false;
      throw err;
    }
  }
  axiosOptions(accessToken) {
    return {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    };
  }
  defaultHeaders(headers) {
    this.$authClient.defaults.headers = {
      ...this.$authClient.defaults.headers,
      ...headers
    };
  }
  async verifyPasswordlessLoginEmail(payload) {
    try {
      return await this.$authClient.post('/login/passwordless', payload);
    } catch (err) {
      throw err;
    }
  }
  async verifyCodeAndLogin(payload) {
    try {
      const response = await this.$authClient.put('/login/passwordless', payload);
      if (response.data.data.access_token) {
        this.setUser(payload.email);
        await this.loginUsingToken(response.data.data.access_token);
      }
      return response;
    } catch (err) {
      throw err;
    }
  }
}